import * as genericService from "./genericFormServices";
import type { CreateAttachmentDto } from "../types/MainType.type";
import { formatSignatureUrl, type FetchParams } from "./NetworkService";
import type { LegalAdviceFormNineData } from "../types/LegalAdvices/LegalAdviceFormNine.type";

const ENDPOINT = "legal-advice-form-nine";

export interface LegalAdviceFormNineFetchResponse {
  data: LegalAdviceFormNineData[];
  totalItems: number;
}

// ---------------------------------------------------------------------------
// 1. Mapper for Legal Advice Form Nine
// ---------------------------------------------------------------------------
export const mapLegalAdviceFormNineBackendToFrontend = (
  item: any
): LegalAdviceFormNineData => {
  if (!item) return {} as LegalAdviceFormNineData;

  const form = item.legalAdviceFormNine || item.formData || item;

  return {
    // Document Level Identifiers
    id: item.id || form.id || "",
    documentNumber: item.documentNumber || "",
    formType: item.formType || 'LEGAL_ADVICE_FORM_NINE',
    status: item.status || form.status || 'DRAFT',

    // Form-Specific Fields
    office: form.office || "",
    lawRegisterNo: form.lawRegisterNo || "",
    refNo: form.refNo || "",

    // Section 1: Case Details (1 to 9)
    courtName: form.courtName || "",
    caseNo: form.caseNo || form.caseNumber || "",
    plaintiffName: form.plaintiffName || "",
    defendantName: form.defendantName || form.defendantNameAndNrc || "",
    caseSummary: form.caseSummary || form.briefFacts || "",
    courtFindingsAndDate: form.courtFindingsAndDate || "",
    preliminaryDecreeDetails: form.preliminaryDecreeDetails || "",
    interimDecreeDetails: form.interimDecreeDetails || "",
    finalDecreeDetails: form.finalDecreeDetails || "",

    // Section 2: Handling Officer Opinion & Signature
    handlingOfficerOpinion: form.handlingOfficerOpinion || "",
    handlingOfficerName: form.handlingOfficerName || form.prosecutorName || "",
    handlingOfficerSubmitDate: form.handlingOfficerSubmitDate || form.prosecutorSubmissionDate || "",
    handlingOfficerSignature: formatSignatureUrl(form.handlingOfficerSignature || form.prosecutorSignatureImage),

    // Section 3: In-Charge Officer Opinion & Signature
    inChargeOfficerOpinion: form.inChargeOfficerOpinion || form.responsibleOfficerOpinion || "",
    inChargeOfficerName: form.inChargeOfficerName || form.officerName || "",
    inChargeOfficerSubmitDate: form.inChargeOfficerSubmitDate || form.officerSubmissionDate || "",
    inChargeOfficerSignature: formatSignatureUrl(form.inChargeOfficerSignature || form.officerSignatureImage),

    // Geo Levels
    originGeoLevel: item.originGeoLevel || "",
    targetGeoLevels: Array.isArray(item.targetGeoLevels) ? item.targetGeoLevels : [],

    // Attachments Array
    attachments: item.attachments || form.attachments || [],

    // Dates & Status Metadata
    rejectionReason: item.rejectionReason || form.rejectionReason || '',
    submitDate: form.submitDate || item.signedAt || item.updatedAt || '',
    createdAt: item.createdAt || form.createdAt || '',
    updatedAt: item.updatedAt || form.updatedAt || '',
    submittedAt: item.submittedAt || form.submittedAt || '',

    // Relational Metadata
    createdBy: item.createdBy
      ? {
          id: item.createdBy.id,
          name: item.createdBy.name,
          email: item.createdBy.email,
          role: item.createdBy.role,
          position: item.createdBy.position,
        }
      : undefined,
    originOffice: item.originOffice
      ? {
          id: item.originOffice.id,
          name: item.originOffice.name,
          code: item.originOffice.code,
        }
      : undefined,
    stageHistory: Array.isArray(item.stageHistory)
      ? item.stageHistory.map((history: any) => ({
          id: history.id,
          action: history.action,
          comments: history.comments,
          attachments: history.attachments,
          createdAt: history.createdAt,
          performedBy: history.performedBy
            ? {
                id: history.performedBy.id,
                name: history.performedBy.name,
                role: history.performedBy.role,
                position: history.performedBy.position,
                office: history.performedBy.office
                  ? {
                      id: history.performedBy.office.id,
                      name: history.performedBy.office.name,
                      code: history.performedBy.office.code,
                      level: history.performedBy.office.level,
                    }
                  : undefined,
              }
            : undefined,
        }))
      : [],
    auditLogs: Array.isArray(item.auditLogs) ? item.auditLogs : [],
  };
};

// ---------------------------------------------------------------------------
// 2. Custom Filter Function for Legal Advice Form Nine
// ---------------------------------------------------------------------------
const legalAdviceFormNineFilterPredicate = (
  app: LegalAdviceFormNineData,
  params: FetchParams
): boolean => {
  const { searchTerm, selectedStatus, selectedDate } = params;

  const matchesStatus = !selectedStatus || app.status === selectedStatus;
  const formattedAppDate = app.submitDate || app.createdAt || '';
  const matchesDate = !selectedDate || Boolean(formattedAppDate.includes(selectedDate));

  const term = (searchTerm || "").toLowerCase().trim();

  const matchesGeneralSearch =
    !term ||
    Boolean(app.documentNumber && app.documentNumber.toLowerCase().includes(term)) ||
    Boolean(app.lawRegisterNo && app.lawRegisterNo.toLowerCase().includes(term)) ||
    Boolean(app.caseNo && app.caseNo.toLowerCase().includes(term)) ||
    Boolean(app.courtName && app.courtName.toLowerCase().includes(term)) ||
    Boolean(app.plaintiffName && app.plaintiffName.toLowerCase().includes(term)) ||
    Boolean(app.defendantName && app.defendantName.toLowerCase().includes(term)) ||
    Boolean(app.handlingOfficerName && app.handlingOfficerName.toLowerCase().includes(term)) ||
    Boolean(app.inChargeOfficerName && app.inChargeOfficerName.toLowerCase().includes(term));

  return Boolean(matchesStatus && matchesDate && matchesGeneralSearch);
};

// ---------------------------------------------------------------------------
// 3. API Request Functions
// ---------------------------------------------------------------------------

export const fetchLegalAdviceFormNineApplications = (
  params: FetchParams
): Promise<LegalAdviceFormNineFetchResponse> =>
  genericService.fetchApplications<LegalAdviceFormNineData>(
    ENDPOINT,
    params,
    mapLegalAdviceFormNineBackendToFrontend,
    legalAdviceFormNineFilterPredicate
  );

export const fetchLegalAdviceFormNineById = (
  id: string
): Promise<LegalAdviceFormNineData> =>
  genericService.fetchApplicationById<LegalAdviceFormNineData>(
    ENDPOINT,
    id,
    mapLegalAdviceFormNineBackendToFrontend
  );

export const saveLegalAdviceFormNineDraft = (
  id: string | null,
  data: Partial<LegalAdviceFormNineData>
) => genericService.saveDraft<LegalAdviceFormNineData>(ENDPOINT, id, data);

export const submitLegalAdviceFormNineForm = (
  id: string,
  data: LegalAdviceFormNineData
) => genericService.submitForm<LegalAdviceFormNineData>(ENDPOINT, id, data);

export const deleteLegalAdviceFormNineForm = (id: string) =>
  genericService.deleteForm(ENDPOINT, id);

// ---------------------------------------------------------------------------
// 4. Superior Stage Actions
// ---------------------------------------------------------------------------
export const startReviewLegalAdviceForm = (id: string) =>
  genericService.startReviewForm(id);

export const attachLegalAdviceForm = (
  id: string,
  comments?: string,
  attachments?: CreateAttachmentDto[],
  targetGeoLevels?: string[]
) => genericService.attachForm(id, comments, attachments, targetGeoLevels);

export const uploadFileService = (file: File) =>
  genericService.uploadFile(file);